import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import moment from 'moment'
import styled from 'styled-components'
import Modal from 'LuxKit/Modal/Modal'
import Counter from 'LuxKit/Counter'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import type { TripItineraryData, TripStop, TripDay } from 'tripPlanner/api/types'
import { doesStayExceedTripDates } from 'tripPlanner/utils/chatItinerary'
import { getDayLabel, getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import ExtendTripModal from './ExtendTripModal'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'

const FieldGroup = styled(Group)`
  flex: 1;
  min-width: 0;
`

const DaySelectButton = styled(TextButton)`
  justify-content: space-between;
`

const FullWidthCounter = styled(Counter)`
  width: 100%;
  justify-content: space-between;
`

interface DayOption {
  dayNumber: number
  date?: string
  cityName: string
}

function buildDayOptionsForStop(stop: TripStop): Array<DayOption> {
  const options = stop.days.map((day: TripDay) => ({
    dayNumber: day.dayNumber,
    date: day.date,
    cityName: stop.placeName,
  }))

  if (options.length === 0) {
    return [{ dayNumber: 1, cityName: stop.placeName }]
  }

  return options
}

interface Props {
  isOpen: boolean
  hotelName: string
  itineraryData: TripItineraryData
  relevantStop?: TripStop
  hotelCity?: string
  defaultDayNumber?: number
  defaultNights?: number
  onClose: () => void
  onSubmit: (message: string) => void
}

function AddHotelToTripModal({
  isOpen,
  hotelName,
  itineraryData,
  relevantStop,
  hotelCity,
  defaultDayNumber = 1,
  defaultNights = 1,
  onClose,
  onSubmit,
}: Props) {
  const intl = useIntl()
  const { isStreaming } = useChatStreamStatus()
  const stops = useMemo(() => itineraryData?.stops ?? [], [itineraryData?.stops])

  const isNewStop = !relevantStop && !!hotelCity
  const targetStop = useMemo(
    () => relevantStop ?? stops[0],
    [relevantStop, stops],
  )

  const dayOptions = useMemo(
    () => !isNewStop && targetStop ? buildDayOptionsForStop(targetStop) : [],
    [isNewStop, targetStop],
  )

  const [selectedDay, setSelectedDay] = useState<DayOption | undefined>(() =>
    dayOptions.find(d => d.dayNumber === defaultDayNumber) ?? dayOptions[0],
  )
  const [nights, setNights] = useState(defaultNights)

  useEffect(() => {
    setSelectedDay(dayOptions.find(d => d.dayNumber === defaultDayNumber) ?? dayOptions[0])
  }, [dayOptions, defaultDayNumber])

  const [isDayDropdownOpen, setIsDayDropdownOpen] = useState(false)
  const [extendState, setExtendState] = useState<{ isOpen: boolean; extraDays: number }>({
    isOpen: false,
    extraDays: 0,
  })

  const dayButtonRef = useRef<HTMLButtonElement>(null)

  const handleDayDropdownOpen = useCallback(() => {
    setIsDayDropdownOpen(true)
  }, [])

  const handleDayDropdownClose = useCallback(() => {
    setIsDayDropdownOpen(false)
  }, [])

  const handleDaySelect = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const dayNumber = Number(e.currentTarget.dataset.dayNumber)
    if (isNaN(dayNumber)) return

    const option = dayOptions.find(d => d.dayNumber === dayNumber)
    if (option) {
      setSelectedDay(option)
    }
    setIsDayDropdownOpen(false)
  }, [dayOptions])

  const handleNightsChange = useCallback((value: number) => {
    setNights(value)
  }, [])

  const buildMessage = useCallback(() => {
    if (isNewStop && hotelCity) {
      const displayMessage = intl.formatMessage(
        {
          id: '-WuBkwGh2RwFaZL5j4NF',
          defaultMessage: 'Add "{hotelName}" to a new stop in {cityName} for {nights, plural, one {# night} other {# nights}}',
          description: 'User chat message shown when adding a hotel to a new trip stop with a specified number of nights',
        },
        { hotelName, cityName: formatPlacePhrase({ location: { name: hotelCity }, language: intl.locale, intent: 'location' }), nights },
      )
      return appendContextTag(
        displayMessage,
        `Add "${hotelName}" to a new stop in ${hotelCity} for ${nights} nights`,
        TRIP_PLANNER_ACTION_CONTEXT_LABEL,
      )
    }

    if (!selectedDay) return undefined
    const endDay = selectedDay.dayNumber + nights
    const fromDisplayLabel = getLocalisedDayLabel(intl, selectedDay.dayNumber, selectedDay.date)
    const fromContextLabel = getDayLabel(selectedDay.dayNumber, selectedDay.date)
    const toDate = selectedDay.date ? moment(selectedDay.date).add(nights, 'days').format('YYYY-MM-DD') : undefined
    const toDisplayLabel = getLocalisedDayLabel(intl, endDay, toDate)
    const toContextLabel = getDayLabel(endDay, toDate)
    const displayMessage = intl.formatMessage(
      {
        id: '05Xk8n84P0XUpCggECPh',
        defaultMessage: 'Assign "{hotelName}" to stay from {fromLabel} to {toLabel} in {cityName}',
        description: 'User chat message shown when assigning a hotel stay to a date range in a specific city',
      },
      {
        hotelName,
        fromLabel: fromDisplayLabel,
        toLabel: toDisplayLabel,
        cityName: formatPlacePhrase({ location: { name: selectedDay.cityName }, language: intl.locale, intent: 'location' }),
      },
    )

    return appendContextTag(
      displayMessage,
      `Assign "${hotelName}" to stay from ${fromContextLabel} to ${toContextLabel} in ${selectedDay.cityName}`,
      TRIP_PLANNER_ACTION_CONTEXT_LABEL,
    )
  }, [hotelName, hotelCity, isNewStop, intl, selectedDay, nights])

  const handleUpdate = useCallback(() => {
    if (!isNewStop && !selectedDay) return

    if (!isNewStop && targetStop && selectedDay) {
      const { exceeds, extraDays } = doesStayExceedTripDates(selectedDay.dayNumber, nights, targetStop)
      if (exceeds) {
        setExtendState({ isOpen: true, extraDays })
        return
      }
    }

    const message = buildMessage()
    if (message) {
      onSubmit(message)
    }
  }, [isNewStop, selectedDay, targetStop, nights, buildMessage, onSubmit])

  const handleExtendConfirm = useCallback(() => {
    setExtendState({ isOpen: false, extraDays: 0 })
    const message = buildMessage()
    if (message) {
      onSubmit(message)
    }
  }, [buildMessage, onSubmit])

  const handleExtendClose = useCallback(() => {
    setExtendState({ isOpen: false, extraDays: 0 })
  }, [])

  const handleClose = useCallback(() => {
    setSelectedDay(dayOptions.find(d => d.dayNumber === defaultDayNumber) ?? dayOptions[0])
    setNights(defaultNights)
    setIsDayDropdownOpen(false)
    setExtendState({ isOpen: false, extraDays: 0 })
    onClose()
  }, [onClose, dayOptions, defaultDayNumber, defaultNights])

  const nightsFormatter = useCallback((value: number) => {
    return intl.formatMessage(
      {
        id: '0Y7CYLTTLQVHQr1XuL12',
        defaultMessage: '{count, plural, one {# night} other {# nights}}',
        description: 'Night count for hotel trip assignment duration selector',
      },
      { count: value },
    )
  }, [intl])

  return (
    <>
      <Modal
        isOpen={isOpen && !extendState.isOpen}
        onClose={handleClose}
        title={intl.formatMessage(tripPlannerGlossaryMessages.addToTrip)}
        subtitle={hotelName}
        primaryActionText={intl.formatMessage({
          id: 'uCjTDWMBDm9S499NCV7s',
          defaultMessage: 'Update',
          description: 'Primary action button to update hotel assignment in trip',
        })}
        onPrimaryActionClick={handleUpdate}
        isPrimaryActionDisabled={(!isNewStop && !selectedDay) || isStreaming}
        secondaryActionText={intl.formatMessage({
          id: 't0-oeWZUrkVajvX2QR3v',
          defaultMessage: 'Cancel',
          description: 'Secondary action button to cancel adding hotel to trip',
        })}
        onSecondaryActionClick={handleClose}
      >
        <Group direction="horizontal" gap={24}>
          {!isNewStop && (
            <FieldGroup direction="vertical" gap={8}>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'dBVesFs_nfMyF8ADPi0T',
                  defaultMessage: 'Check-in',
                  description: 'Label for the check-in day selection field when adding a hotel to trip',
                })}
              </BodyText>
              <DaySelectButton
                ref={dayButtonRef}
                kind="secondary"
                size="large"
                endIcon={<LineAngleDownIcon />}
                onClick={handleDayDropdownOpen}
                fit="full-width"
              >
                {selectedDay ? getLocalisedDayLabel(intl, selectedDay.dayNumber, selectedDay.date) : intl.formatMessage({
                  id: 'f6OvnZs4BqTTYR5XNvmx',
                  defaultMessage: 'Select day',
                  description: 'Placeholder text when no check-in day is selected for hotel trip assignment',
                })}
              </DaySelectButton>
              <DropdownList
                open={isDayDropdownOpen}
                onClose={handleDayDropdownClose}
                triggerRef={dayButtonRef}
                anchorRef={dayButtonRef}
                size="S"
              >
                {dayOptions.map(option => (
                  <ClickableListItem
                    key={option.dayNumber}
                    size="medium"
                    title={getLocalisedDayLabel(intl, option.dayNumber, option.date)}
                    subtitle={option.cityName}
                    data-day-number={option.dayNumber}
                    onClick={handleDaySelect}
                    selected={option.dayNumber === selectedDay?.dayNumber}
                  />
                ))}
              </DropdownList>
            </FieldGroup>
          )}
          <FieldGroup direction="vertical" gap={8}>
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'Ryy4iNU6jcD3-4W9eFz1',
                defaultMessage: 'Length of stay',
                description: 'Label for the number of nights selection field when adding a hotel to trip',
              })}
            </BodyText>
            <FullWidthCounter
              value={nights}
              min={1}
              max={30}
              onChange={handleNightsChange}
              size="large"
              valueFormatter={nightsFormatter}
            />
          </FieldGroup>
        </Group>
      </Modal>

      <ExtendTripModal
        isOpen={extendState.isOpen}
        hotelName={hotelName}
        extraDays={extendState.extraDays}
        onClose={handleExtendClose}
        onConfirm={handleExtendConfirm}
      />
    </>
  )
}

export default AddHotelToTripModal
